import {
    BaseColumnDefinition,
    BoardCommandDataRow,
    BoardDefinition,
    CommandResult,
    ControlDefinition,
    Environment,
    GridControlTypes,
} from "Service/Client";
import { GridSettings } from "Configuration";
import { CommandService, DatabaseImageService, DropDownService, GridDefinitionService, TextBlockService } from "Service";
import { DataConstants } from "Constants";
import { AppContext, Util } from "Core";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { CssClass, DisplayModes, TemplateTypes } from "Enums";
import { IDataRow } from "Entities";
import { IControl } from "Controls/Interfaces";
import { EventArgs } from "Core/Events";
import { CONTROL_ID_ATTRIBUTE_NAME } from "Controls/Control";
import { BaseGrid } from "./BaseGrid";
import CancellationToken from "cancellationtoken";
import { DatabaseImage } from "Controls/DatabaseImage";

export class Board extends BaseGrid {
    /**
     * @hidden
     */
    public dmsControlDictionary: { [controlId: number]: IControl } = {};

    /**
     * @hidden
     */
    public dmsColumnDictionary: { [columnName: string]: BaseColumnDefinition } = {};

    /**
     * Holds the default values of each control.
     * @hidden
     */
    protected fields: { [fieldName: string]: Object | null } = {};

    /**
     * @hidden
     */
    public dmsControlTemplateDictionary: { [controlId: number]: string } = {};

    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/Board.html").default;

    /**
     * @hidden
     */
    public static contentTemplate: string = require("./Templates/BoardContent.html").default;

    /**
     * @hidden
     */
    public static columnTemplate: string = require("./Templates/BoardColumn.html").default;

    /**
     * @hidden
     */
    public static columnsTemplate: string = require("./Templates/BoardColumns.html").default;

    /**
     * @hidden
     */
    protected _kendoElement: kendo.ui.TaskBoard;

    /**
     * @hidden
     */
    protected boardDefinition: BoardDefinition;

    public boardColumn: (data: any) => string;
    public boardColumns: (data: any) => string;

    protected controlDefinitionDict: { [index: number]: ControlDefinition } = {};

    constructor(
        gridDefinition: BoardDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        environment: Environment,
        comboboxService: DropDownService,
        commandService: CommandService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService,
    ) {
        super(
            gridDefinition,
            textBlockCategory,
            gridSettings,
            environment,
            comboboxService,
            commandService,
            gridDefinitionService,
            textBlockService,
            databaseImageService,
        );
        this.boardDefinition = gridDefinition;
        this.isAutoResize = Util.stringIsNullOrEmpty(gridDefinition.GridHeight);
        this.boardColumn = kendo.template(this.gridSettings.getTemplate(TemplateTypes.Column), { useWithBlock: false });
        this.boardColumns = kendo.template(this.gridSettings.getTemplate(TemplateTypes.Columns), { useWithBlock: false });
    }

    protected async setElementInternal(element: JQuery): Promise<void> {
        await this.textBlockService.getAll();

        const dataSourceOptions: kendo.data.DataSourceOptions = {
            transport: {
                read: (options: kendo.data.DataSourceTransportRead) => {
                    this.setBusy(true);
                    this.commandService
                        .get(this.baseGridDefinition.GridID, options.data, this.cancellationTokenSource.token)
                        .then((result) => {
                            this.storeOriginalDataRow(result.value, options.data.group.length > 0);
                            try {
                                (options.success as JQuery.Ajax.SuccessCallback<any>)(result, null, null);
                            } catch (e) {
                                console.log(e);
                            }
                        })
                        .catch(() => (options.error as JQuery.Ajax.ErrorCallback<any>)(null, "error", "error"));
                },
                create: function (options: kendo.data.DataSourceTransportCreate) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
                update: function (options: kendo.data.DataSourceTransportUpdate) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
                destroy: function (options: kendo.data.DataSourceTransportDestroy) {
                    (options.success as JQuery.Ajax.SuccessCallback<any>)(null, null, null);
                },
            },
            schema: {
                aggregates: function (data: CommandResult) {
                    return data.aggregateresults;
                },
                data: function (data: CommandResult) {
                    return data.value;
                },
                total: function (data: CommandResult) {
                    return data["odata.count"];
                },
                groups: function (data: CommandResult) {
                    return data.value;
                },
                model: {
                    id: DataConstants.ROW_INDEX_COLUMN_NAME,
                },
                errors: "error",
            },
            serverPaging: true,
            serverSorting: true,
            serverFiltering: true,
            serverGrouping: true,
            serverAggregates: true,
            batch: false,
            autoSync: false,
            change: (e) => {
                if (e.action === "sync") {
                    this.onSync((<any>e).changedItems);
                }
            },
        };

        const columns = this.boardDefinition.Groups.map((group) => {
            return { text: this.textBlockService.get(group.GroupHeader, this.textBlockCategory), status: group.GroupName };
        });
        const columnsDataSource = new kendo.data.DataSource({
            data: columns,
        });

        // Initialize controls
        const templateData = this.initializeRows(this.boardDefinition.Columns);
        if (templateData.modelExtensions) {
            $.extend(dataSourceOptions.schema.model, templateData.modelExtensions);
        }

        const template = kendo.template(this.gridSettings.getTemplate(TemplateTypes.Content), { useWithBlock: false });
        // const renderedTemplate = template(this);

        if (!this.boardDefinition.CanReorderColumns) {
            this.jQueryElement.addClass("dms-board-noreorder");
        }

        const options: kendo.ui.TaskBoardOptions = {
            columns: columnsDataSource,
            columnSettings: {
                width: this.boardDefinition.GridWidth || 300,
            },
            dataSource: new kendo.data.DataSource(dataSourceOptions),
            dataStatusField: this.boardDefinition.GroupColumnName,
            dataOrderField: this.boardDefinition.OrderColumnName,
            reorderable: this.boardDefinition.CanReorderColumns || false,
            height: this.height || 1,
            autoBind: false,
            editable: false,
            template: (dataRow: IDataRow) =>
                template({ DataRow: dataRow, boardDefinition: this.boardDefinition, gridSettings: this.gridSettings, Board: this }),
            toolbar: false,
            dataBound: (e) => this.onDatabound(e),
        };
        (<any>options).cardMenu = false;

        this._kendoElement = new kendo.ui.TaskBoard(this.jQueryElement[0], options);
        columnsDataSource.read();
    }

    private reloadPromise: Promise<void>;

    private onDatabound(e: kendo.ui.TaskBoardDataBoundEvent): void {
        const root = this._kendoElement.wrapper;

        this.initializeRowControls(root, "." + CssClass.link, null, null, null, null);
        this.initializeRowControls(root, "." + CssClass.command, null, null, null, null);

        this.initializeImages(root);

        this.initializeTooltips();
    }

    /**
     * Reloads data of this grid.
     * @hidden
     */
    protected async reloadInternal(): Promise<void> {
        this.disposeEditContext();
        this.show();

        if (this.cancellationTokenSource && this.reloadPromise) {
            this.cancellationTokenSource.cancel("test");
            try {
                await this.reloadPromise;
            } catch {}
        }
        this.cancellationTokenSource = CancellationToken.create();
        this.reloadPromise = Promise.resolve(this._kendoElement.dataSource.read({ reloadFromDataSource: true }));
        // setTimeout(() => {
        //     const progress = this._kendoElement.element.find(".k-loading-mask");
        //     if (progress) {
        //         progress.append($("<button>Cancel</button>"));
        //     }
        // }, 1000);)

        await this.reloadPromise;
    }

    /**
     * Initialize rows for this form.
     * @param columns List of columns.
     * @param fields Fields which will be filled.
     * @hidden
     */
    protected initializeRows(columns: BaseColumnDefinition[][]): { template: string; modelExtensions: Object } {
        let template = "";
        let modelExtensions: Object = {};
        if (!columns) {
            return { template, modelExtensions };
        }

        for (const row of columns) {
            for (const column of row) {
                // empty cells are perfectly fine
                if (column == null) {
                    continue;
                }

                this.dmsColumnDictionary[column.ColumnName] = column;
                for (const controlDefinition of column.Controls) {
                    this.controlDefinitionDict[controlDefinition.ControlID] = controlDefinition;
                    // Initialize control
                    let control = AppContext.current.initializeControl(controlDefinition, this.textBlockCategory, this.environment);

                    // Initialize filter fields for view model
                    let fieldName = this.getControlFieldName(controlDefinition);

                    if (fieldName) {
                        if (this.fields[fieldName] == null) {
                            // field not already existing
                            this.fields[fieldName] = control.getDefaultValue();
                        }
                    }
                    if (controlDefinition.OnDisplay == null || controlDefinition.OnDisplay) {
                        let controlTemplate = control.getTemplate(DisplayModes.Display, fieldName);

                        this.dmsControlTemplateDictionary[controlDefinition.ControlID] = controlTemplate ? controlTemplate.template : "";
                        if (controlTemplate != null) {
                            if (controlTemplate.modelExtension != null) {
                                $.extend(modelExtensions, controlTemplate.modelExtension);
                            }
                        }
                    }

                    this.dmsControlDictionary[controlDefinition.ControlID] = control;
                }
            }
        }
        return { template, modelExtensions };
    }

    /**
     * Set the height of the grid.
     * @param height Height in pixel.
     * @since 4.0.0
     */
    public setHeight(height: string | number): void {
        super.setHeight(height);
        if (this._kendoElement) {
            this._kendoElement.wrapper.height(height);
            this._kendoElement.resize(true);
        }
    }

    // Initializes all buttons in the given grid with the given cssClass and applies the onClickFunction
    private initializeRowControls(
        rootElement: JQuery,
        cssClass: string,
        onClickFunction: (control: IControl) => void,
        beforeCommandFunction: (e: EventArgs<IControl>) => void,
        afterCommandFunction: (e: EventArgs<IControl>) => void,
        afterCommandCancelFunction: (e: EventArgs<IControl>) => void,
    ): void {
        let buttons = rootElement.find(cssClass); // get all buttons with the given css class from the grid

        for (const button of buttons) {
            let jQueryButton = $(button);
            let controlID = parseInt(jQueryButton.attr(CONTROL_ID_ATTRIBUTE_NAME));
            let controlDefinition = this.controlDefinitionDict[controlID];
            let buttonElement = AppContext.current.initializeControl(controlDefinition, this.textBlockCategory, this.environment);
            buttonElement.on(ControlEventTypes.Resolve, (e) => this.onResolveScript(e.sender, <string>e.data));
            buttonElement.on(ControlEventTypes.GetDataRow, (e) => this.getDataRow(e.sender));
            buttonElement.setElement(jQueryButton);
            if (onClickFunction != null) {
                buttonElement.on(ControlEventTypes.Click, (e) => onClickFunction(e.sender));
            }

            if (beforeCommandFunction != null) {
                buttonElement.on(ControlEventTypes.BeforeCommand, beforeCommandFunction);
            }

            if (afterCommandFunction != null) {
                buttonElement.on(ControlEventTypes.AfterCommand, afterCommandFunction);
            }

            if (afterCommandCancelFunction != null) {
                buttonElement.on(ControlEventTypes.AfterCommandCancel, afterCommandCancelFunction);
            }

            // this.setRowControlEvent(buttonElement, cssClass);
        }
    }

    private async onSync(items: IDataRow[]): Promise<void> {
        this.setBusy(true);
        const dataRows: BoardCommandDataRow[] = items.map((item) => {
            return {
                GroupName: <string>item[this.boardDefinition.GroupColumnName],
                Order: Number(item[this.boardDefinition.OrderColumnName]),
                DataRow: item,
            };
        });
        await this.commandService.setBoardCommand(this.baseGridDefinition.GridID, this.boardDefinition.CommandControlID, dataRows);
        await this.reload();
    }

    public getCurrentDataRow(): IDataRow {
        return undefined;
    }

    /**
     * Get the dataRow for a given control.
     * @param control Control in edit-row.
     * @hidden
     */
    protected getDataRow(control: IControl): IDataRow {
        const card = $(control.getElement()).closest(".k-taskboard-card");
        return <IDataRow>(<any>this._kendoElement.dataItem(card));
    }

    /**
     * Event occurs when a control requests to resolve a script.
     * @param control Control which requested resolving.
     * @param script The script which needs to be resolved.
     */
    private onResolveScript(control: IControl, script: string): string {
        const data = this.getDataRow(control);
        return super.resolve(script, data);
    }

    private initializeImages(rootElement: JQuery) {
        let hasImages = false;
        for (const controlId in this.controlDefinitionDict) {
            const ctrl = this.controlDefinitionDict[controlId];
            if (ctrl.ControlTypeID == GridControlTypes.DatabaseImage) {
                hasImages = true;
            }
        }

        if (!hasImages) {
            return;
        }

        const cards = this._kendoElement.items();

        if (cards.length == 0) {
            return;
        }
        for (let j = 0, len = cards.length; j < len; j++) {
            const card = $(cards[j]);

            const controls: IControl[] = [];

            const images = card.find(`.${CssClass.databaseImage}`);

            for (let i = 0, cnt = images.length; i < cnt; i++) {
                const image = images[i];
                let jQueryImage = $(image);
                let controlID = parseInt(jQueryImage.attr(CONTROL_ID_ATTRIBUTE_NAME));
                let controlDefinition = this.controlDefinitionDict[controlID];
                let imageElement = AppContext.current.initializeControl(controlDefinition, this.textBlockCategory, this.environment);
                imageElement.on(ControlEventTypes.Resolve, (e) => this.onResolveScript(e.sender, <string>e.data));
                imageElement.on(ControlEventTypes.GetDataRow, (e) => this.getDataRow(e.sender));
                imageElement.setElement(jQueryImage);
                controls.push(imageElement);
            }

            let dataItem = this._kendoElement.dataItem(card);
            DatabaseImage.loadMultiple(controls, this.databaseImageService, this.baseGridDefinition.GridID, <IDataRow>(<any>dataItem));
        }
    }
}
